import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import type {
	ImportHeldRowResolution,
	ImportRowMatchModel,
	ImportRowUndoModel,
	ImportRowUndoResult,
	ImportRowWarningsModel,
	ImportRunRowStatus,
} from '../import/import-run.models';
import { BaseEntity } from './base.entity';
import { ImportRunEntity } from './import-run.entity';

/**
 * One data row of an import run (PD-12353): the original cells, the mapped
 * payload, and the outcome. Held rows (duplicate matches) keep their payload
 * here until the user resolves them (skip / create separately / update).
 *
 * Record ids are plain columns (no FK constraint) because the framework is
 * entity-generic — the importerKey on the parent run says which table they
 * point at.
 */
@Index('idx_import_run_row_run_status', ['importRunId', 'status'])
// auditable: false - one bookkeeping row per imported data row; auditing them
// would double every import's write volume with noise.
@Entity('import_run_row', {}, { auditable: false })
export class ImportRunRowEntity extends BaseEntity {
	@Column('int', { name: 'fk_import_run__id', unsigned: true })
	public importRunId!: number;

	@ManyToOne(() => ImportRunEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_import_run__id', referencedColumnName: 'id' }])
	public importRun!: ImportRunEntity | null;

	/** 1-based data-row number in the source file (excluding the header row). */
	@Column('int', { name: 'rowNumber', unsigned: true })
	public rowNumber!: number;

	/** Original cells keyed by source header, exactly as uploaded (for the error/report download). */
	@Column('json', { name: 'rawData' })
	public rawData!: Record<string, string>;

	/** Mapped importable-field values (field id → normalized cell value). */
	@Column('json', { name: 'payload' })
	public payload!: Record<string, string>;

	@Column('enum', {
		name: 'status',
		enum: ['pending', 'created', 'updated', 'skipped', 'held', 'error'],
		default: 'pending',
	})
	public status!: ImportRunRowStatus;

	/** Candidate existing records this row matched (why it was held). */
	@Column('json', { name: 'matchedRecords', nullable: true })
	public matchedRecords!: Array<ImportRowMatchModel> | null;

	/** Record created by this row (create path or 'create separately' resolution). */
	@Column('int', { name: 'createdRecordId', unsigned: true, nullable: true })
	public createdRecordId!: number | null;

	/** Existing record written by an 'update' resolution. */
	@Column('int', { name: 'updatedRecordId', unsigned: true, nullable: true })
	public updatedRecordId!: number | null;

	/** How a held row was resolved (null while still held / for never-held rows). */
	@Column('enum', {
		name: 'resolution',
		enum: ['skip', 'create', 'update'],
		nullable: true,
	})
	public resolution!: ImportHeldRowResolution | null;

	/** Non-fatal notes (e.g. unmatched salesperson name). */
	@Column('json', { name: 'warnings', nullable: true })
	public warnings!: ImportRowWarningsModel | null;

	@Column('text', { name: 'errorMessage', nullable: true })
	public errorMessage!: string | null;

	/** PD-17603 — for an updated row, what it changed (so the import can be undone). */
	@Column('json', { name: 'undoData', nullable: true })
	public undoData!: ImportRowUndoModel | null;

	/** PD-17603 — set once the row's undo has run, so a retried undo skips it. */
	@Column('varchar', { name: 'undoResult', length: 16, nullable: true })
	public undoResult!: ImportRowUndoResult | null;
}
